import { AmountValueLocalized, Translate } from '@components'

import { amountToDisplayValue, Color, DonutChart, DonutChartData, Link, SkeletonText, Text } from '@billy/design-system'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { EmberRoute } from '../../../../enums/emberRoute'
import { useOrganizationSource } from '../../../../hooks'
import { useEmberRouteToUrlGenerator } from '../../../../hooks/routing/useEmberRouteToUrlGenerator'
import { getOrganizationViewUrl } from '../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { widgetId } from '../../constants/widgetId'
import { useWidgetsActions } from '../../hooks/useWidgetsActions'
import { StatsContact } from '../../services/query-api'
import * as Styled from './styles'

interface UserData extends StatsContact, DonutChartData {}

export enum UsersListContext {
  Vendor = 'vendor',
  Customer = 'customer',
}

interface UsersListProps {
  isLoading?: boolean
  users: UserData[]
  usersListContext?: UsersListContext
}

const LOADING_DATA = [...new Array(3)]

export const UsersList = ({ isLoading, users, usersListContext }: UsersListProps) => {
  const { organization } = useUserOrganization()
  const { generateUrl } = useEmberRouteToUrlGenerator()
  const { isSourceBilly } = useOrganizationSource()

  const totalAmount = useMemo(() => users.reduce((accumulator, { value }) => accumulator + value, 0), [users])
  const data = useMemo(() => users.map((user) => ({ ...user, value: Math.abs(user.value) })), [users])
  const navigate = useNavigate()
  const { trackDashboardActionPerformed } = useWidgetsActions()

  const handleUserClick = useCallback(
    (contactId: string, index: number) => {
      if (usersListContext === UsersListContext.Vendor) {
        const vendorUrl = generateUrl?.(EmberRoute.ContactsSupplier, contactId)

        if (vendorUrl) {
          trackDashboardActionPerformed({ action: index + 1, widgetId: widgetId.TopVendors })
          navigate(vendorUrl)
        }
      }
      if (usersListContext === UsersListContext.Customer) {
        const customerUrl = isSourceBilly
          ? generateUrl?.(EmberRoute.ContactsCustomer, contactId)
          : getOrganizationViewUrl(organization?.url, reactRoute.organization.clients.getViewClientRoute(contactId))

        if (customerUrl) {
          trackDashboardActionPerformed({ action: index + 1, widgetId: widgetId.TopCustomers })
          navigate(customerUrl)
        }
      }
    },
    [generateUrl, isSourceBilly, navigate, organization?.url, trackDashboardActionPerformed, usersListContext],
  )

  return (
    <Styled.Wrapper>
      <DonutChart data={data} isLoading={isLoading}>
        <Text alignment="center" colorVariant="secondary" variant="bodySM">
          <Translate value="total" />
        </Text>
        <AmountValueLocalized
          amount={totalAmount}
          currencyId={organization?.baseCurrencyId || ''}
          textVariant="h2"
          shortFormat
          alignCenter
        />
      </DonutChart>
      {isLoading ? (
        <Styled.List>
          {LOADING_DATA.map((_, index) => (
            <Styled.ListItem key={index} color={Color.Grey200}>
              <Styled.Label>
                <SkeletonText width={100} />
              </Styled.Label>
              <Styled.Amount>
                <SkeletonText width={40} />
              </Styled.Amount>
            </Styled.ListItem>
          ))}
        </Styled.List>
      ) : (
        <Styled.List>
          {users.map(({ color, contactId, amount, name }, index) => (
            <Styled.ListItem key={contactId} color={color}>
              <Styled.Label title={name} truncate>
                <Link onClick={() => handleUserClick(contactId, index)} withAnimation underlined={false}>
                  {name}
                </Link>
              </Styled.Label>
              <Styled.Amount>
                {amountToDisplayValue(amount, true)} {organization?.baseCurrencyId}
              </Styled.Amount>
            </Styled.ListItem>
          ))}
        </Styled.List>
      )}
    </Styled.Wrapper>
  )
}
